import { Link } from "react-router-dom";
import {
  Building2, Handshake, SprayCan, Wrench, LifeBuoy, ClipboardList,
  BarChart3, Rocket, Network, Boxes, ArrowUpRight, Check,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import CTABanner from "../components/CTABanner";
import Crossover from "../components/Crossover";
import Mandala from "../components/Mandala";
import { Reveal } from "../components/motion";
import useMeta from "../lib/useMeta";
import { breadcrumb, serviceSchema } from "../lib/seo";
import { SEGMENTS } from "../lib/site";

const PILLAR_FLOW = ["Coordinate", "Monitor", "Resolve", "Audit", "Review", "Improve"];

const SERVICES_LIST = [
  { icon: Building2, title: "Workplace & Facility Operations", text: "Day-to-day coordination of workplace services and operating readiness." },
  { icon: Handshake, title: "Vendor Management", text: "Vendor onboarding, coordination, SLA tracking, escalation and reviews." },
  { icon: SprayCan, title: "Housekeeping, Security & Pantry Coordination", text: "Service scheduling, quality checks, manpower/vendor coordination and issue resolution." },
  { icon: Wrench, title: "Repairs & Maintenance", text: "Preventive maintenance schedules, breakdown coordination and closure tracking." },
  { icon: LifeBuoy, title: "Helpdesk & Issue Management", text: "Structured issue logging, ownership, escalation and closure." },
  { icon: ClipboardList, title: "SOPs & Operational Controls", text: "Checklists, SOPs, escalation matrices and operating routines." },
  { icon: BarChart3, title: "MIS & Operations Dashboards", text: "Monthly review, service metrics, open issues and vendor performance." },
  { icon: Rocket, title: "New Site / Office Setup", text: "Mobilisation, vendor setup, supplies, equipment, readiness and launch support." },
  { icon: Network, title: "Multi-Site Operations", text: "Standardisation, governance and review cadence across distributed locations." },
  { icon: Boxes, title: "Asset Coordination", text: "Asset registers, maintenance tracking, replacement planning and movement visibility." },
];

const PROCESS = [
  { num: "01", title: "Understand", text: "Map the sites, vendors, requirements and existing pain points." },
  { num: "02", title: "Design", text: "Define SOPs, service levels, responsibilities and reporting structure." },
  { num: "03", title: "Run", text: "Coordinate daily operations, vendors, maintenance and service delivery." },
  { num: "04", title: "Review", text: "Monthly MIS, issue trends and vendor/service performance." },
  { num: "05", title: "Improve", text: "Correct recurring problems, optimise costs and standardise processes." },
];

const SETUP_CHECKLIST = [
  "Furniture & workstations", "IT & networking", "Pantry & beverages", "Housekeeping readiness",
  "Electrical & MRO", "Safety & compliance", "Signage & branding", "Recurring supplies",
];

export default function WorkplaceOperations() {
  useMeta({
    title: "Workplace Operations Services | Saarthva Ventures",
    description: "Saarthva supports inventory audits, replenishment, vendor coordination, facilities requirements, site readiness and multi-location operations.",
    path: "/workplace-operations",
    schemas: [
      breadcrumb("Workplace Operations", "/workplace-operations"),
      serviceSchema({
        name: "Workplace Operations Services",
        description: "Saarthva supports inventory audits, replenishment, vendor coordination, facilities requirements, site readiness and multi-location operations.",
        path: "/workplace-operations",
      }),
    ],
  });
  return (
    <div data-testid="workplace-operations-page">
      <PageHeader
        eyebrow="OPERATIONS AS A SERVICE"
        title="Your Operations Team."
        accent="Without building one from scratch."
        description="Saarthva helps businesses coordinate the everyday operational work required to keep workplaces and multi-site businesses running — through one accountable operating partner."
      />

      <section className="bg-white border-b border-line" data-testid="wo-pillar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <Reveal>
            <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy max-w-3xl leading-tight">
              One accountable partner — <span className="text-saffron">from requirement to execution.</span>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-2.5" data-testid="wo-pillar-flow">
              {PILLAR_FLOW.map((s, i) => (
                <span key={s} className="flex items-center gap-2.5">
                  <span className="rounded-full border border-line bg-mist text-navy text-[13px] font-semibold px-4 py-2">{s}</span>
                  {i < PILLAR_FLOW.length - 1 && <span className="text-saffron font-bold">→</span>}
                </span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="wo-talk-cta"
                className="inline-flex items-center gap-2 rounded-full bg-saffron hover:bg-saffrondark text-white font-semibold px-7 py-3.5 transition-colors"
              >
                Talk to Saarthva <ArrowUpRight className="w-4 h-4" />
              </Link>
              <a
                href="#wo-services"
                data-testid="wo-explore-cta"
                className="inline-flex items-center gap-2 rounded-full border border-navy/20 hover:border-saffron text-navy font-semibold px-7 py-3.5 transition-colors"
              >
                Explore What We Can Manage
              </a>
            </div>
            <p className="mt-6 text-sm text-slate-600 max-w-2xl" data-testid="wo-related-links">
              Operations support pairs naturally with <Link to="/managed-procurement" className="font-semibold text-saffron hover:text-navy transition-colors">managed procurement</Link> — and you can browse the <Link to="/products" className="font-semibold text-saffron hover:text-navy transition-colors">procurement categories</Link> we supply across India.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white scroll-mt-24" id="wo-services" data-testid="wo-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-12">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">WHAT WE CAN MANAGE</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy max-w-2xl leading-tight">
              The invisible engine behind smooth workplaces.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES_LIST.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title} delay={(i % 3) * 0.06}>
                  <div className="h-full rounded-2xl border border-line bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-slate-200 hover:border-steel/40" data-testid={`wo-service-${i}`}>
                    <span className="w-11 h-11 rounded-xl bg-cream grid place-items-center mb-5">
                      <Icon className="w-5 h-5 text-saffron" strokeWidth={1.8} />
                    </span>
                    <h3 className="font-display text-lg font-bold text-navy leading-snug">{s.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-navy text-white grain relative overflow-hidden" data-testid="wo-process">
        <Mandala className="absolute -right-40 -bottom-48 w-[520px] h-[520px] text-steel opacity-40 spin-slow" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-14">
            <p className="font-mono text-[11px] tracking-[0.3em] text-saffron mb-4">HOW OPERATIONS AS A SERVICE WORKS</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight leading-tight max-w-2xl">
              Understand. Design. Run. Review. Improve.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-10">
            {PROCESS.map((step, i) => (
              <Reveal key={step.num} delay={i * 0.08}>
                <div className="border-t border-white/15 pt-6" data-testid={`wo-step-${step.num}`}>
                  <p className="font-mono text-saffron text-sm tracking-widest">{step.num}</p>
                  <h3 className="mt-3 font-display text-xl font-bold">{step.title}</h3>
                  <p className="mt-2 text-sm text-slate-400 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-16 font-display text-2xl sm:text-3xl font-bold tracking-tight max-w-3xl leading-snug border-l-2 border-saffron pl-6" data-testid="wo-pull-quote">
              We don't just recommend what should happen. We help make sure it happens.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-mist" data-testid="wo-new-site">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">NEW SITE / OFFICE SETUP</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy leading-tight">
              Opening a new office or location?
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-lg">
              Give Saarthva the checklist. We coordinate sourcing and operational readiness across furniture,
              IT, pantry, housekeeping, electrical, safety, signage, vendors and recurring supplies.
            </p>
            <Link
              to="/request-quote?type=New-site setup"
              data-testid="wo-new-site-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy hover:bg-saffron text-white font-semibold px-7 py-3.5 transition-colors"
            >
              Discuss a New Site Setup <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="rounded-2xl bg-navy text-white p-8">
              <p className="font-mono text-[10px] tracking-[0.3em] text-saffron mb-5">THE SETUP CHECKLIST</p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {SETUP_CHECKLIST.map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-slate-200">
                    <Check className="w-4 h-4 text-saffron shrink-0" strokeWidth={2.5} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <Crossover />

      <section className="bg-white" data-testid="wo-segments">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Reveal className="mb-10">
            <p className="font-mono text-[11px] tracking-[0.3em] text-steel mb-4">IDEAL FOR</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-navy">Wherever work happens.</h2>
          </Reveal>
          <div className="flex flex-wrap gap-3">
            {SEGMENTS.map((seg, i) => {
              const Icon = seg.icon;
              return (
                <Reveal key={seg.label} delay={i * 0.03}>
                  <span className="inline-flex items-center gap-2.5 rounded-full border border-line bg-white px-5 py-3 text-sm font-semibold text-navy transition-colors hover:border-saffron/50 hover:bg-cream" data-testid={`wo-segment-${i}`}>
                    <Icon className="w-4 h-4 text-steel" strokeWidth={1.8} />
                    {seg.label}
                  </span>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABanner
        title="Your operations team is"
        accent="one conversation away."
        copy="Tell us which sites, vendors or routines are eating your team's time — we will take it from there."
      />
    </div>
  );
}
